import { ENDPOINTS } from "../../constant/routes";
import { request } from "../../services/requests";

export interface PayoutRecord {
  paid_at: string;
  paid_by_email: string | null;
  payout_reference: string;
  amount_paid: number | null;
  currency: string | null;
  note: string | null;
}

export interface WithdrawalRequest {
  withdrawal_id: string;
  user_id: string;
  username: string | null;
  email: string | null;
  amount: number;
  /** The seller's currency; the table itself has no currency column */
  currency: string;
  /** The seller's withdrawn earnings span several currencies, so `amount` mixes them */
  currency_mixed: boolean;
  request_date: string;
  status: "Paid" | "Pending" | string;
  /** How it was paid; null if unpaid, or paid before payout records were kept */
  payout: PayoutRecord | null;
  /** The orders this request paid out; null when they can't be matched for sure */
  earnings?: PayoutEarningLine[] | null;
}

/** One order in a payout, in the seller's earnings currency:
 * sale − fee − voucher_share = amount (seller_profit is already net of the voucher). */
export interface PayoutEarningLine {
  order_id: string;
  sale: number;
  /** Inflos' fee on this order */
  fee: number;
  fee_rate: number | null;
  /** standard | brought_buyer (0%) | brought_buyer_partial (part at 0%) */
  fee_reason: string;
  /** The seller's share of a welcome voucher on this order; 0 when none */
  voucher_share: number;
  voucher_code: string | null;
  /** What the seller gets */
  amount: number;
  currency: string;
  created_at: string | null;
}

export interface SellerBalance {
  currency: string;
  available: number;
  pending: number;
  in_withdrawal: number;
}

/** Where to pay a seller, as saved by them on the marketplace. The account
 * number / PayNow id is only `ciphertext` (RSA-OAEP under the owner's key);
 * the dashboard decrypts it in the browser once the owner unlocks. */
export interface LockedPayoutAccount {
  method: "bank" | "paynow";
  country: string;
  account_holder_name: string;
  bank_name: string | null;
  /** SWIFT/BIC, for banks outside Singapore */
  bank_code: string | null;
  paynow_type: "mobile" | "uen" | null;
  last4: string;
  ciphertext: string;
  key_id: string;
  created_at: string | null;
  updated_at: string | null;
  last_change: { changed_at: string | null; old_last4: string | null; new_last4: string | null } | null;
}

export interface SellerPayoutContext {
  /** null when the seller hasn't saved payout details */
  payout_account?: LockedPayoutAccount | null;
  currency: string;
  country: string | null;
  /** Fallback contact for sellers without payout details */
  whatsapp_number: string | null;
  balances: SellerBalance[];
  /** Earnings locked into withdrawal requests (newest first, up to 30) */
  withdrawing_earnings: { order_id: string; amount: number; currency: string; created_at: string }[];
  /** Paid out so far, per currency */
  paid: { currency: string; amount: number; count: number }[];
}

export interface PayoutsResponse {
  /** false until the withdrawal_payouts migration has been applied */
  payouts_recorded: boolean;
  requests: WithdrawalRequest[];
  sellers: Record<string, SellerPayoutContext>;
}

export const getWithdrawalRequests = (token: string) =>
  request(token).get<PayoutsResponse>(`${ENDPOINTS.TRANSACTIONS}/withdrawal-requests`);

export interface MarkPaidPayload {
  payout_reference: string;
  /** ISO datetime; defaults to now */
  paid_at?: string;
  note?: string;
}

export const markWithdrawalPaid = (withdrawalId: string, payload: MarkPaidPayload, token: string) =>
  request(token).put<{ withdrawal_id: string; status: string; payout_recorded: boolean; already_paid?: boolean }>(
    `${ENDPOINTS.TRANSACTIONS}/withdrawal-requests/${encodeURIComponent(withdrawalId)}/paid`,
    payload
  );

/** Tell the server an unlock happened (decryption is in the browser, so it can't see it). */
export const logPayoutUnlock = (sellerIds: string[], where: string, token: string) =>
  request(token).post(`${ENDPOINTS.TRANSACTIONS}/payout-accounts/unlocked`, { seller_ids: sellerIds, where });
